import { Link, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useBreadcrumbLabel } from '@components/layout/breadcrumbLabelStore'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'

/*
 * Spec 214 §3.5. Bare `/reports` is Dashboards, so the "Reports" crumb opens the report
 * catalog explicitly, on every page; the Advanced Reports tab has its own address.
 */
export const REPORTS_CRUMB_PATH = '/reports?mode=reports'
export const ADVANCED_REPORTS_CRUMB_PATH = '/reports?mode=reports&tab=advanced'
// The generic word for an Advanced Report whose page has not published its name yet — the
// same word the page's own loading shell uses (exagoPromptFields' ADVANCED_REPORT_NAME,
// not imported here so the app's chrome does not pull in a report page's module graph).
const ADVANCED_REPORT_FALLBACK = 'Advanced Report'
// The catalog's Advanced Reports tab, as a crumb. `isLast: false` always: no page this band
// draws IS that tab (it is /reports?…), so this crumb is a link wherever it sits.
const ADVANCED_REPORTS_CRUMB = { label: 'Advanced Reports', path: ADVANCED_REPORTS_CRUMB_PATH, isLast: false }

const labelMap = {
  dashboard: 'Dashboard',
  matters: 'Matters',
  contacts: 'Contacts',
  calendar: 'Calendar',
  documents: 'Documents',
  billing: 'Billing',
  'time-entries': 'Time Entries',
  'time-sheet': 'Time Sheet',
  'pre-bills': 'Pre-Bills',
  reports: 'Reports',
  payments: 'Payments',
  settings: 'Settings',
  objects: 'Objects',
  // A segment with no entry here falls through to the RAW path segment, so the
  // Report Center's own sub-routes were rendering as "queue" and "schedules"
  // (lower-case, mid-trail) next to properly-cased siblings.
  queue: 'Queue',
  schedules: 'Schedules',
  // /reports/queue/packs read "… / Queue / packs". The crumb is the page's own h1
  // (ReportPacks.jsx), which Header.jsx also titles the route.
  packs: 'Report packs',
}

function useEntityName(type, id) {
  const queryClient = useQueryClient()
  if (!type || !id) return null
  const cached = queryClient.getQueryData([type, id])
  return cached?.name || cached?.fullName || null
}

const Breadcrumbs = () => {
  const { pathname } = useLocation()
  // What the page itself says its last crumb should be (a report NAME). Read
  // unconditionally: hooks may not sit behind the early return below.
  const publishedLabel = useBreadcrumbLabel(pathname)
  const segments = pathname.split('/').filter(Boolean)

  // Detect entity page pattern like /matters/:id
  const entityType = segments.length >= 2 ? segments[0] : null
  const entityId = segments.length >= 2 ? segments[1] : null
  const isEntityRoute = entityType
    && !labelMap[entityId]
    && entityId?.length > 8
  const entityName = useEntityName(
    isEntityRoute ? entityType.replace(/s$/, '') : null,
    isEntityRoute ? entityId : null
  )

  /*
   * Report detail (/reports/:id) is now drawn by THIS band like every other
   * route, instead of by a second in-page trail in ReportActionBar. The app had
   * two breadcrumb implementations with different markup, different tokens and
   * the same aria-label; the Queue page got a full-width white strip and a report
   * page got a borderless line sharing a row with its action buttons. One trail,
   * one place.
   *
   * The reason it could not simply be un-suppressed before: this band builds its
   * crumbs from PATH SEGMENTS, so /reports/190 would read "Home / Reports / 190" —
   * the raw wire id. The page therefore publishes its own last crumb through
   * `breadcrumbLabelStore`, keyed by pathname. ReportActionBar keeps the action
   * cluster and no longer renders a trail.
   *
   * While the name is still loading — or on a report page that early-returns
   * before publishing (invalid id / blocked / not found) — nothing is published
   * and the crumb falls back to the generic word "Report". Never the id: a number
   * in a trail is meaningless to a reader and is the wire key besides.
   */
  const isReportDetail =
    segments[0] === 'reports' && segments.length === 2 && /^\d+$/.test(segments[1])

  /*
   * Spec 214 §3.5: an Advanced Report (/reports/exago/:guid) read "Home / Reports / exago /
   * <guid>", and "exago" linked to /reports/exago, which renders "Invalid report id". It
   * reads "Home / Reports / Advanced Reports / <name>": the middle crumb is the catalog's
   * Advanced Reports tab, the last is the name the page published (else the generic word),
   * never the content id, and nothing links to /reports/exago.
   *
   * The name is the FIRM's own (Exago report names are firm-authored), so that crumb carries
   * PENDO_IGNORE, as the page's heading does (spec 209 §3).
   */
  const isExagoDetail = segments[0] === 'reports' && segments[1] === 'exago' && segments.length === 3

  if (segments.length < 2) return null

  const crumbs = segments.map((seg, idx) => {
    /*
     * The "exago" segment is never a page of its own: bare /reports/exago is ReportView
     * reading "exago" as a report id ("Invalid report id"). It read as a raw, unlinked "exago";
     * it is the catalog's Advanced Reports tab, and a LINK even as the last crumb — the page
     * on screen is not that tab, so it is never marked the current page. Only the trail of a
     * real Advanced Report (/reports/exago/:id, below) ends in a name.
     */
    if (idx === 1 && segments[0] === 'reports' && seg === 'exago') return ADVANCED_REPORTS_CRUMB

    // The Reports crumb opens the catalog, never Dashboards (spec 214 §3.5).
    const path = idx === 0 && seg === 'reports' ? REPORTS_CRUMB_PATH : '/' + segments.slice(0, idx + 1).join('/')
    const isLast = idx === segments.length - 1
    let label = labelMap[seg] || seg

    // A report detail route: the numeric segment is the report id, which must
    // never be shown. Use the name the page published, else the generic word.
    if (isReportDetail && idx === 1) label = publishedLabel || 'Report'

    // If this segment is an entity ID, try to show the name
    if (idx > 0 && isEntityRoute && idx === 1 && entityName) {
      label = entityName
    }

    return { label, path, isLast }
  })

  const trail = isExagoDetail
    ? [
      crumbs[0],
      ADVANCED_REPORTS_CRUMB,
      { label: publishedLabel || ADVANCED_REPORT_FALLBACK, path: pathname, isLast: true, firmText: true },
    ]
    : crumbs

  /*
   * A1-scope (2026-08-26) — full-bleed band, gutter-aligned contents.
   *
   * The white strip and its bottom rule stay on <nav> so the band spans the
   * window; the trail itself moves into a `.report-shell` row, the same class
   * the report content uses inside <main>. That is what makes "Home / Reports /
   * Billable Hours" start on exactly the same pixel as the report title below
   * it and the tab strip above it — previously this band was a px-6 sibling
   * ABOVE main, so it never saw the shell's gutter at all and sat 16px to the
   * left of the content on a wide screen (66px after the A1 increase).
   *
   * `.report-shell` expects a 24px-padded parent (it is written against main's
   * p-6); the nav's own px-6 supplies that here, so the totals match: 82px from
   * 640px up, 90px from 1280px up.
   */
  return (
    <nav aria-label="Breadcrumb" className="px-6 py-2 text-xs text-gray-500 border-b border-gray-100 bg-white">
      <div className="report-shell flex items-center gap-1.5">
        <Link to="/" className="hover:text-gray-600 transition-colors">
          Home
        </Link>
        {trail.map((crumb) => (
          <span key={crumb.path} className="flex items-center gap-1.5">
            <span aria-hidden="true">/</span>
            {crumb.isLast ? (
              <span className={`text-gray-600 font-medium${crumb.firmText ? ` ${PENDO_IGNORE}` : ''}`} aria-current="page">{crumb.label}</span>
            ) : (
              <Link
                to={crumb.path}
                className="hover:text-gray-600 transition-colors"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        ))}
      </div>
    </nav>
  )
}

export default Breadcrumbs
